import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AlertTriangle, ArrowLeft, Mail, Search } from 'lucide-react';
import { assetPath } from '../utils/appPaths';

const BRAND_LOGO_WIDTH = 480;
const BRAND_LOGO_HEIGHT = 200;

const PageShell = styled.section`
  position: relative;
  min-height: calc(100vh - var(--header-height));
  padding: clamp(32px, 6vw, 88px) 0;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at top left, rgba(44, 159, 164, 0.18), transparent 38%),
    radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.14), transparent 42%),
    linear-gradient(180deg, #f8fafc 0%, #eef6ff 100%);
  pointer-events: none;
`;

const ContentWrap = styled.div`
  position: relative;
`;

const Card = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: clamp(28px, 4vw, 48px);
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 28px;
  box-shadow: 0 24px 50px -28px rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(10px);
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const BrandLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #0f172a;
  font-weight: 700;
  font-size: 1rem;

  &:hover {
    color: #2c9fa4;
  }
`;

const Logo = styled.img.attrs({
  width: BRAND_LOGO_WIDTH,
  height: BRAND_LOGO_HEIGHT,
  decoding: 'async',
})`
  height: 48px;
  width: auto;
`;

const CodeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #ecfeff;
  border: 1px solid #a5f3fc;
  color: #0f766e;
  font-size: 0.92rem;
  font-weight: 700;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(260px, 0.85fr);
  gap: 28px;

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const CopyBlock = styled.div`
  display: grid;
  gap: 18px;
`;

const Eyebrow = styled.p`
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2c9fa4;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(2.35rem, 4.5vw, 4rem);
  line-height: 1.05;
  color: #0f172a;
`;

const Description = styled.p`
  margin: 0;
  max-width: 34rem;
  font-size: 1.08rem;
  line-height: 1.75;
  color: #475569;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

const ButtonLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  padding: 0 18px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const PrimaryButton = styled(ButtonLink)`
  background: #2c9fa4;
  color: white;

  &:hover {
    background: #1c6568;
  }
`;

const SecondaryButton = styled(ButtonLink)`
  background: white;
  color: #334155;
  border: 1px solid #cbd5e1;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }
`;

const SidePanel = styled.aside`
  padding: 22px;
  border-radius: 22px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
`;

const PanelTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 1.12rem;
  color: #0f172a;
`;

const PanelText = styled.p`
  margin: 0 0 16px;
  color: #64748b;
  line-height: 1.65;
`;

const TipList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 12px;
`;

const TipItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #334155;
  line-height: 1.6;
`;

const TipIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
`;

const NotFound = () => {
  return (
    <PageShell>
      <BackgroundGlow />
      <div className="container">
        <ContentWrap>
          <Card>
            <TopRow>
              <BrandLink to="/">
                <Logo src={assetPath('/logo.png')} alt="MicroPinouts" />
                MicroPinouts
              </BrandLink>

              <CodeBadge>
                <AlertTriangle size={18} />
                Error 404
              </CodeBadge>
            </TopRow>

            <MainGrid>
              <CopyBlock>
                <Eyebrow>Lost In The Pin Map</Eyebrow>
                <Title>That page could not be found.</Title>
                <Description>
                  The link may be outdated, the board slug may have changed, or the page never existed in this build.
                  You can head back to the main catalog, browse wiring, or send us the broken link so we can fix it.
                </Description>

                <ActionRow>
                  <PrimaryButton to="/">
                    <ArrowLeft size={18} />
                    Back to Home
                  </PrimaryButton>

                  <SecondaryButton to="/wiring-guides">
                    <Search size={18} />
                    Browse Wiring
                  </SecondaryButton>

                  <SecondaryButton to="/contact">
                    <Mail size={18} />
                    Contact Us
                  </SecondaryButton>
                </ActionRow>
              </CopyBlock>

              <SidePanel>
                <PanelTitle>Quick checks</PanelTitle>
                <PanelText>
                  If you expected a real pinout or guide here, these are the fastest ways to get back on track.
                </PanelText>

                <TipList>
                  <TipItem>
                    <TipIconWrap>1</TipIconWrap>
                    <span>Double-check the URL for an outdated slug, missing character, or extra trailing text.</span>
                  </TipItem>

                  <TipItem>
                    <TipIconWrap>2</TipIconWrap>
                    <span>Start from the home page search if you were looking for a board, sensor, or IC pinout.</span>
                  </TipItem>

                  <TipItem>
                    <TipIconWrap>3</TipIconWrap>
                    <span>Use the contact page if a public link inside MicroPinouts sent you here unexpectedly.</span>
                  </TipItem>
                </TipList>
              </SidePanel>
            </MainGrid>
          </Card>
        </ContentWrap>
      </div>
    </PageShell>
  );
};

export default NotFound;
